import { heroDelivery, heroPreloadLinks } from "@/lib/hero-delivery";
import { RichText } from "@/components/site/RichText";
import { LocaleLink, type PublicPath } from "@/components/site/LocaleLink";
import { createFileRoute } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { PageHero } from "@/components/site/PageHero";
import { Section } from "@/components/site/Section";
import { Overline } from "@/components/site/Overline";
import { ServiceBand } from "@/components/site/ServiceBand";
import { ProcessSteps } from "@/components/site/ProcessSteps";
import { Ruler, Paintbrush, Gem, KeyRound, ShieldCheck, ArrowRight } from "lucide-react";
import nieuwbouwHero from "../../assets/nieuwbouw-hero.avif";
import nieuwbouwHeroSmall from "../../assets/nieuwbouw-hero-640.avif";
import { FaqSection } from "@/components/site/FaqSection";
import { faqs, faqJsonLd } from "@/content/faqs";
import { canonical, breadcrumbJsonLd, serviceJsonLd, shareImageMeta, langPageMeta } from "@/content/site";
import { publishedPageQuery } from "@/lib/content-queries";
import { pageContent } from "@/lib/page-content";
import { usePageContent, validateVoorvertoning } from "@/lib/use-page-content";
import { PhotoBlocks } from "@/components/site/PhotoBlocks";

const PATH = "/nieuwbouw";
const SLEUTEL = "nieuwbouw";

const HERO_FALLBACK = { src: nieuwbouwHero, srcSet: `${nieuwbouwHeroSmall} 640w, ${nieuwbouwHero} 1024w` };

const CODE_META_TITLE = "Bouwkundig zwembad bouwen in Limburg | Gopro Projects";
const CODE_META_DESCRIPTION =
  "Bouwkundige zwembaden op maat met 3 cm Recticel isolatie en polyester of vinylester bekleding in kleur naar keuze. Van grondwerken tot boordsteen, in Limburg.";

export const Route = createFileRoute("/$lang/nieuwbouw")({
  staticData: { sitemap: true },
  validateSearch: validateVoorvertoning,
  loader: async ({ context, params }) => ({
    page: await context.queryClient.ensureQueryData(publishedPageQuery(SLEUTEL, params.lang)),
  }),
  head: ({ params, loaderData }) => {
    const c = pageContent(loaderData?.page);
    const title = c.metaTitle(CODE_META_TITLE);
    const description = c.metaDescription(CODE_META_DESCRIPTION);
    const heroPhoto = heroDelivery(c.section("hero"), HERO_FALLBACK);
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        ...langPageMeta(params.lang, PATH).meta,
        { name: "twitter:card", content: "summary_large_image" },
        ...shareImageMeta("nieuwbouw", "Nieuwbouw bouwkundig zwembad met isolatie en polyester bekleding"),
      ],
      links: [...langPageMeta(params.lang, PATH).links, ...heroPreloadLinks(heroPhoto)],
      scripts: [
        faqJsonLd(faqs.nieuwbouw),
        breadcrumbJsonLd("Nieuwbouw zwembaden", PATH),
        serviceJsonLd({
          name: "Bouw van bouwkundige zwembaden",
          serviceType: "Zwembadbouw",
          description:
            "Bouwkundige zwembaden op maat, geïsoleerd met 3 cm Recticel en afgewerkt met polyester of vinylester bekleding, inclusief technieken, boordstenen en terras.",
          path: PATH,
        }),
      ],
    };
  },
  component: NieuwbouwPage,
});

const pillarIcons = [Ruler, Paintbrush, Gem, KeyRound];

const pillars = [
  {
    titel: "Kuipconstructie",
    tekst:
      "Stabiele betonkuip op maat, berekend op uw grond- en tuinsituatie. Standaard geïsoleerd met 3 cm Recticel voor een comfortabel en energiezuinig zwembad.",
  },
  {
    titel: "Vinylester & polyester",
    tekst:
      "Duurzame, naadloze bekleding met uitstekende kleurvastheid. Vinylester is extra osmosebestendig. Keuze uit een mooi aanbod van kleuren.",
  },
  {
    titel: "Boordstenen & terras",
    tekst:
      "Natuursteen, keramiek of beton voor een strakke rand rondom het bad. Indien gewenst leggen wij ook een bijhorend terras aan.",
  },
  {
    titel: "Sleutel-op-de-deur",
    tekst:
      "Wij regelen ontwerp, grondwerken, techniek, afwerking en oplevering in één traject. Of begeleiden de zelfbouwer met plaatsing van onze bekleding.",
  },
];

const whyUs = [
  "3 cm dikke Recticel-isolatie als standaard",
  "Polyester of vinylester bekleding — vinylester is beter osmosebestendig",
  "Keuze uit een mooi aanbod van kleuren",
  "Totaalproject van grondwerken tot laatste boordsteen",
  "Begeleiding van zelfbouwers mogelijk",
  "Optioneel bijhorend terras",
];

const steps = [
  {
    titel: "1. Adviesgesprek en opmeting",
    tekst:
      "Wij komen ter plaatse, bekijken de tuin, de grondsituatie en de toegang voor machines, en bespreken vorm, afmetingen, trap, zitbank en de plaats van de technische ruimte.",
  },
  {
    titel: "2. Grondwerken en uitgraving",
    tekst:
      "De put wordt uitgegraven, de ondergrond gestabiliseerd en de afvoer van grond geregeld. Ook de leidingsleuven naar de technische ruimte worden nu voorzien.",
  },
  {
    titel: "3. Betonkuip op maat",
    tekst:
      "De bouwkundige kuip wordt opgebouwd in gewapend beton, met de trap, zitbank of plage meteen mee ingewerkt. Zo bent u volledig vrij in vorm en afmetingen.",
  },
  {
    titel: "4. Isolatie met 3 cm Recticel",
    tekst:
      "Vloer en wanden worden volledig bekleed met 3 cm Recticel isolatie. Deze stap onderscheidt onze baden: het water blijft langer warm en de warmtepomp verbruikt minder.",
  },
  {
    titel: "5. Polyester of vinylester bekleding",
    tekst:
      "De kuip wordt in meerdere lagen gelamineerd tot één naadloos geheel, daarna geschuurd en afgewerkt met een topcoat in de kleur van uw keuze.",
  },
  {
    titel: "6. Technieken en verlichting",
    tekst:
      "Filtratie, leidingwerk, skimmers, inspuiters, LED-verlichting, warmtepomp en eventueel een rolluik of overkapping worden geplaatst en getest.",
  },
  {
    titel: "7. Boordstenen, terras en oplevering",
    tekst:
      "De boordstenen worden geplaatst, het terras afgewerkt, het bad gevuld en de waterbalans ingesteld. Bij de oplevering leggen wij de volledige installatie rustig uit.",
  },
];

const priceFactors = [
  {
    titel: "Afmetingen en vorm",
    tekst:
      "Lengte, breedte, diepte en eventuele trap, zitbank of plage bepalen hoeveel grondwerk, beton en bekleding nodig is.",
  },
  {
    titel: "Grondsituatie en bereikbaarheid",
    tekst:
      "Rotsige of natte grond, grondwater, hellend terrein of een moeilijk bereikbare tuin maken het grondwerk arbeidsintensiever.",
  },
  {
    titel: "Type bekleding",
    tekst: "Polyester of het extra osmosebestendige vinylester, en de gekozen kleur van de topcoat.",
  },
  {
    titel: "Technieken",
    tekst:
      "Filtratiekeuze, zoutelektrolyse, verlichting, tegenstroominstallatie, warmtepomp en automatische waterbehandeling.",
  },
  {
    titel: "Afdekking",
    tekst:
      "Een OCEA rolluik in de plage, in een nis of bovengronds, of een AURA overkapping — elk met een eigen prijskaartje.",
  },
  {
    titel: "Afwerking rondom",
    tekst:
      "Boordstenen in natuursteen, keramiek of beton, en de omvang van het aansluitende terras of de tuinaanleg.",
  },
];

function NieuwbouwPage() {
  const { lang } = Route.useParams();
  const { voorvertoning } = Route.useSearch();
  const c = usePageContent(SLEUTEL, lang, voorvertoning);
  const hero = c.section("hero");
  const heroPhoto = heroDelivery(hero, HERO_FALLBACK);

  return (
    <div className="flex flex-col">
      <PageHero
        title={c.veld("hero", "titel", "Nieuwbouw zwembaden op maat")}
        subtitle={c.rijkInline("hero",
          "Van een eerste schets tot de laatste boordsteen: wij bouwen uw bouwkundig zwembad volledig op maat, in heel Limburg en Zuid-Nederland. Elke kuip wordt standaard geïsoleerd met 3 cm Recticel en afgewerkt met polyester of vinylester in een kleur naar keuze.",
        )}
        imageSrc={heroPhoto.src}
        imageFocal={heroPhoto.focal}
        imageSrcSet={heroPhoto.srcSet}

        imageAlt={c.veld(
          "hero",
          "alt",
          "Nieuwbouw van een bouwkundig zwembad met polyester bekleding in een tuin in Limburg",
        )}
        cta={{
          label: c.veld("hero", "knopTekst", "Vraag een offerte"),
          to: c.veld("hero", "knopLink", "/offerte") as PublicPath,
        }}
        secondaryCta={{
          label: c.veld("hero", "knop2Tekst", "Bekijk realisaties"),
          to: c.veld("hero", "knop2Link", "/realisaties") as PublicPath,
        }}
      />

      <Section tone="light">
        <div className="max-w-[70ch]">
          <Overline>{c.veld("wat-is", "overline", "Bouwkundig bouwen")}</Overline>
          <h2 className="mt-4 display-section">{c.veld("wat-is", "titel", "Wat is een bouwkundig zwembad?")}</h2>
          {c
            .alineas("wat-is", [
              "Een bouwkundig zwembad is een zwembad dat ter plaatse in gewapend beton wordt gebouwd en daarna wordt geïsoleerd en bekleed. In tegenstelling tot een kant-en-klare kuip bent u volledig vrij in vorm, afmetingen, diepte en de plaats van trap, zitbank of plage.",
              "Bij Gopro Projects krijgt die betonkuip standaard 3 cm Recticel isolatie op vloer en wanden, en daarna een naadloze polyester of vinylester bekleding met gekleurde topcoat. Dat levert een zwembad op dat warmte vasthoudt, geen plooien of lasnaden vertoont en na jaren nog perfect kleurvast is. Wij voeren het volledige traject uit als één totaalproject — u hebt maar één aanspreekpunt.",
            ])
            .map((alinea, index) => (
              <p key={alinea.slice(0, 24)} className={`${index === 0 ? "mt-6" : "mt-4"} text-[17px] leading-7 text-on-surface-variant`}>
                <RichText waarde={alinea} inline />
              </p>
            ))}
        </div>
      </Section>

      <ServiceBand
        items={c.kaarten("pijlers", pillars).map((kaart, index) => ({
          icon: pillarIcons[index % pillarIcons.length]!,
          title: kaart.titel,
          text: kaart.tekst,
        }))}
      />

      <ProcessSteps
        overline={c.veld("proces", "overline", "Stap voor stap")}
        title={c.veld("proces", "titel", "Zo verloopt de bouw, stap voor stap")}
        intro={c.rijkInline("proces",
          "Een bouwkundig zwembad bouwen wij in zeven duidelijke fasen. U weet op elk moment waar we staan en wat de volgende stap is.",
        )}
        steps={c.kaarten("proces", steps).map((stap) => ({ title: stap.titel, text: stap.tekst }))}
      />

      <Section tone="mist" compact>
        <Button asChild variant="outline">
          <LocaleLink to={c.veld("opbouw-link", "knopLink", "/opbouw-zwembad") as PublicPath}>
            {c.veld("opbouw-link", "knopTekst", "Bekijk een volledige opbouw in foto's")}{" "}
            <ArrowRight className="ml-2 h-4 w-4" />
          </LocaleLink>
        </Button>
      </Section>

      <Section tone="light">
        <div className="grid items-start gap-12 lg:grid-cols-2 lg:gap-16">
          <div>
            <Overline>{c.veld("waarom", "overline", "Onze keuzes")}</Overline>
            <h2 className="mt-4 display-section">{c.veld("waarom", "titel", "Waarom onze bouwkundige baden?")}</h2>
            <p className="mt-6 max-w-[70ch] text-[17px] leading-7 text-on-surface-variant">
              {c.rijkInline("waarom",
                "Wij bouwen geen standaard kuipen, maar comfortabele, duurzame zwembaden. De combinatie van isolatie, osmosebestendige bekleding en kleurvrijheid maakt elk project uniek.",
              )}
            </p>
            <ul className="mt-8 divide-y divide-border border-y border-border text-on-surface-variant">
              {c.lijst("waarom", whyUs).map((item) => (
                <li key={item} className="flex items-start gap-3 py-4">
                  <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-brand-deep-water" strokeWidth={1.75} />
                  <span className="text-sm leading-6">{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-primary p-8 text-primary-foreground lg:p-10">
            <Overline onDark>{c.veld("zelfbouw", "overline", "Zelfbouw")}</Overline>
            <h3 className="mt-4 font-display text-xl font-semibold">
              {c.veld("zelfbouw", "titel", "Ook voor zelfbouwers")}
            </h3>
            <p className="mt-4 text-[17px] leading-7 text-primary-foreground/80">
              {c.rijkInline("zelfbouw",
                "Wilt u zelf de grondwerken of de betonkuip doen? Wij begeleiden u doorheen het project en plaatsen daarna onze polyester of vinylester zwembadbekleding in uw eigen kuip. Zo combineert u de besparing van zelfbouw met de zekerheid van een professionele, waterdichte afwerking — inclusief advies over isolatie, doorvoeren, skimmers en leidingwerk vóór u begint.",
              )}
            </p>
          </div>
        </div>
      </Section>

      <Section tone="mist">
        <div className="max-w-[70ch]">
          <Overline>{c.veld("prijs", "overline", "Prijsopbouw")}</Overline>
          <h2 className="mt-4 display-section">
            {c.veld("prijs", "titel", "Wat bepaalt de prijs van een bouwkundig zwembad?")}
          </h2>
          <p className="mt-6 text-[17px] leading-7 text-on-surface-variant">
            {c.rijkInline("prijs",
              "Elk zwembad is maatwerk, dus een eerlijke prijs geven wij pas na een plaatsbezoek. Deze zes factoren wegen het zwaarst door in de uiteindelijke offerte.",
            )}
          </p>
        </div>
        <div className="mt-12 grid gap-px bg-border sm:grid-cols-2 lg:grid-cols-3">
          {c.kaarten("prijs", priceFactors).map((factor) => (
            <article key={factor.titel} className="bg-background p-6">
              <h3 className="font-display text-lg font-semibold">{factor.titel}</h3>
              <p className="mt-2 text-sm leading-6 text-on-surface-variant">{factor.tekst}</p>
            </article>
          ))}
        </div>
      </Section>

      <section className="bg-primary py-16 text-primary-foreground lg:py-20">
        <div className="mx-auto w-full max-w-[1280px] px-5 lg:px-16">
          <div className="flex flex-col justify-between gap-8 lg:flex-row lg:items-center">
            <p className="max-w-[70ch] text-[17px] leading-7 text-primary-foreground/80">
              {c.rijkInline("cta",
                "Bezorg ons uw gewenste afmetingen en enkele foto's van de tuin. Wij komen langs, bespreken de mogelijkheden en maken een duidelijke offerte zonder verrassingen achteraf.",
              )}
            </p>
            <Button asChild size="lg" className="bg-secondary text-secondary-foreground hover:bg-secondary/90">
              <LocaleLink to={c.veld("cta", "knopLink", "/offerte") as PublicPath}>
                {c.veld("cta", "knopTekst", "Gratis offerte aanvragen")} <ArrowRight className="ml-2 h-4 w-4" />
              </LocaleLink>
            </Button>
          </div>
        </div>
      </section>
      <PhotoBlocks content={c} />
      <FaqSection items={faqs.nieuwbouw} />
    </div>
  );
}
